<template>
  <div
    class="product-card"
    :class="[
      $blockClass(blockClass, 'product-card'),
      { [$class('loading')]: !product },
    ]"
    tabindex="0"
  >
    <a
      class="product-card__link"
      :href="selectedVariantUrl"
    >
      <div
        ref="carousel"
        class="product-card__splide splide"
        :js-carousel="carouselId"
      >
        <div class="splide__track">
          <ul class="splide__list">
            <li class="splide__slide">
              <responsive-image
                :class="$blockClass(blockClass, 'product-card-image')"
                block-class="product-card"
                :image="selectedVariantImage"
                min-max="300-820"
                ratio="4:5"
                :sizes="sizes"
              />
            </li>
            <li class="splide__slide">
              <responsive-image
                v-if="altVariantImageUrl"
                :class="$blockClass(blockClass, 'product-card-image')"
                block-class="product-card"
                :image="{ src: altVariantImageUrl }"
                min-max="300-820"
                ratio="4:5"
                :sizes="sizes"
              />
            </li>
          </ul>
        </div>
      </div>

      <responsive-image
        v-if="altVariantImageUrl"
        :class="$blockClass(blockClass, 'product-card-image-alt')"
        block-class="product-card-alt"
        :image="{ src: altVariantImageUrl }"
        min-max="300-820"
        ratio="4:5"
        :sizes="sizes"
      />

      <div
        class="product-card__information"
        :class="[
          $blockClass(blockClass, 'product-card-information'),
          { 'product-card__information--with-alt-image': altVariantImageUrl },
        ]"
      >
        <div
          class="product-card__content"
          :class="$blockClass(blockClass, 'product-card-content')"
        >
          <p
            class="product-card__title"
            :class="$blockClass(blockClass, 'product-card-title')"
            v-text="product.title"
          />

          <p
            class="product-card__prices"
            :class="$blockClass(blockClass, 'product-card-prices')"
            v-html="formattedPrice"
          />
        </div>

        <product-badge
          v-if="enableBadges"
          :class="$blockClass(blockClass, 'product-card-badge')"
          :product="product"
          block-class="product-card"
        />
      </div>
    </a>

    <product-swatches
      v-if="enableSwatches && colorOptions"
      product-card
      :option="colorOptions"
      :options="options"
      :product="product"
      block-class="product-swatches--location-product-card"
      :swatch-behavior="swatchBehavior"
      is-collection
      @option-change="updateVariant"
      @rich-product-data-fetched="storeRichProductData"
    />
  </div>
</template>

<script>

/**
 * Vue: Product card
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Number} index - Product card's forloop index.
 * @param {Boolean} enableBadges - Displays badges on product card.
 * @param {Boolean} enableMobileGallery - Displays badges on product card.
 * @param {Boolean} enableSwatches - Displays swatches on product card.
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @param {String} sizes - Responsive image sizes.
 *
 */
import Carousel from '~/api/carousel'
import ResponsiveImage from '~global/images/responsive-image'
import ProductSwatches from '~async/product-swatches/product-swatches'
import ProductBadge from '~async/product-badge/product-badge'

export default {
  name: 'ProductCard',

  components: {
    ProductBadge,
    ProductSwatches,
    ResponsiveImage,
  },

  props: {
    containerId: {
      type: String,
      default: '',
    },
    blockClass: {
      type: String,
      default: '',
    },
    enableBadges: {
      type: Boolean,
      default: true,
    },
    enableMobileGallery: {
      type: Boolean,
      default: true,
    },
    enableSwatches: {
      type: Boolean,
      default: true,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    collectionUrl: {
      type: String,
      default: '',
    },
    sizes: {
      type: [String, null],
      default: null,
    },
    swatchBehavior: {
      type: Boolean,
      default: false,
    },

  },
  data() {
    return {
      richProductData: null,
      hasChangedVariants: false,
      selectedVariantImage: this.product.featuredImage,
      altVariantImageUrl: null,
      selectedVariant: this.product ? this.product.variants[0] : null,
      selectedVariantUrl: this.product.url,
      slide: {
        carousel: false,
        config: {
          arrows: false,
          pagination: false,
          perMove: 1,
          omitEnd: true,
          snap: true,
          speed: 800,
          easing: 'ease-in-out',
          type: 'slide',
          wheel: false,
        },
      },
    }
  },

  computed: {
    carouselId() {
      return [
        this.containerId,
        this.product.id,
      ].filter(Boolean).join('-')
    },
    formattedPrice() {
      if (!this.product) {
        return ''
      }

      const priceParts = []

      if (this.product.price.varies && this.hasChangedVariants === false) {
        priceParts.push(`${this.$string('product.prices.from')}`)
      }

      if (this.selectedVariant) {
        return this.formatSelectedVariantPrice()
      }

      if (this.product.compareAtPrice && this.product.compareAtPrice.min) {
        priceParts.push(`<s>${this.$formatMoney(this.product.compareAtPrice.min)}</s>`)
        priceParts.push(`<span class="product-card__prices--sale">${this.$formatMoney(this.product.price.min)}</span>`)
      } else {
        priceParts.push(`${this.$formatMoney(this.product.price.amount)}`)
      }

      return priceParts.join(' ')

    },

    /**
     * Computed color options.
     * @returns {Object}
     */
    colorOptions() {
      if (!this.product) {
        return {}
      }

      return this.options.find((option) => {
        const optionName = option.name?.toLowerCase()
        if (!optionName) {
          return false
        }
        const colorOptionLabels = this.$variable('productOptionsName.colorOption').toLowerCase().split(',')
        return colorOptionLabels.find(label => optionName.includes(label))
      })
    },

    /**
     * Computed options.
     * - Fallback to liquid object.
     * @returns {Array}
     */
    options() {
      if (!this.product) {
        return []
      }

      const rawHiddenVariantIdList = this.product.metafields
        ? this.product.metafields
          .find(metafield => metafield.namespace === 'custom' && metafield.key === 'hidden_variants')?.value
        : (this.product.hiddenVariants && this.product.hiddenVariants.value) || null

      if (!rawHiddenVariantIdList) {
        return this.product.options
      }

      const hiddenVariantIdList = JSON.parse(rawHiddenVariantIdList)
        .map(globalVariantId => globalVariantId.split('/ProductVariant/')[1])
        .map(Number)

      const visibleVariants = this.product.variants.filter(variant => {
        return !hiddenVariantIdList.includes(variant.id)
      })

      const filteredAllOptions = this.product.options.map((option, optionIndex) => {
        return {
          ...option,
          values: option.values.map(optionValue => {
            if (optionIndex === 0) {
              const visibleVariantsWithOptionValue = visibleVariants.filter(variant => {
                return variant.selectedOptions[optionIndex].value === optionValue
              })

              return visibleVariantsWithOptionValue.length > 0 ? optionValue : null
            }

            return optionValue
          }).filter(Boolean),
        }
      })

      return filteredAllOptions
    },

    /**
     * Compute the collection url to get product url with collection alias.
     * @returns {String}
     */
    productUrl() {
      let url = `/products/${this.product.handle}`

      if (this.collectionUrl) {
        url = this.collectionUrl + url
      }

      return url
    },
  },

  mounted() {
    this.initCarousel()
  },

  methods: {

    formatSelectedVariantPrice() {
      const priceParts = []

      if (this.hasChangedVariants === false) {
        if (this.product.price.varies) {
          priceParts.push(`${this.$string('product.prices.from')}`)
        }

        const lowestPrice = this.product.price.min
        const highestPrice = 0

        if (highestPrice > lowestPrice) {
          priceParts.push(`<s>${this.$formatMoney(highestPrice)}</s>`)
          priceParts.push(`<span class="product-card__prices--sale">${this.$formatMoney(lowestPrice)}</span>`)
        } else {
          priceParts.push(`${this.$formatMoney(lowestPrice)}`)
        }

        return priceParts.join(' ')
      }

      const lowestPrice = this.selectedVariant.price.amount

      const highestPrice =
        this.selectedVariant.compareAtPrice &&
        this.selectedVariant.compareAtPrice.amount > lowestPrice
          ? this.selectedVariant.compareAtPrice.amount
          : this.selectedVariant.price.amount

      if (highestPrice > lowestPrice) {
        priceParts.push(`<s>${this.$formatMoney(highestPrice)}</s>`)
        priceParts.push(`<span class="product-card__prices--sale">${this.$formatMoney(lowestPrice)}</span>`)
      } else {
        priceParts.push(`${this.$formatMoney(lowestPrice)}`)
      }

      return priceParts.join(' ')
    },

    storeRichProductData({ formattedProduct }) {
      this.richProductData = formattedProduct
    },

    /**
   * Updates the image and variant information based on the selected color.
   * Sets the selected color image and variant,
   * and updates the URL with the variant ID if available.
   * @param {Object} param - The parameter object.
   * @param {String} param.variantImage - The image URL of the selected variant.
   * @param {Object} param.variant - The selected variant object.
   */
    updateVariant({ variantImage, variant, initialRun }) {
      if (!initialRun) {
        this.hasChangedVariants = true
      }

      this.selectedVariantImage = variantImage
      this.altVariantImageUrl = variant.lifestyleAssetUrl
      this.selectedVariant = variant

      const varUrl = this.selectedVariant?.id
        ? `${this.product.url}?variant=${this.selectedVariant.id}`
        : `${this.product.url}`

      this.selectedVariantUrl = varUrl

      this.initCarousel()
    },

    /**
     * Initialize Carousel
     */
    initCarousel() {
      if (!this.$refs.carousel) {
        return
      }

      if (!this.carouselId) {
        return
      }

      if (this.slide.carousel) {
        this.slide.carousel.destroy()
      }

      const isGalleryEnabled = this.enableMobileGallery && this.altVariantImageUrl

      this.slide.carousel = Carousel(`[js-carousel="${this.carouselId}"]`, {
        ...this.slide.config,
        noDrag: isGalleryEnabled ? false : '*',
      })

      this.slide.carousel.mount()
    },
  },
}
</script>

<style lang="scss">
@import "./product-card";
</style>
